<template>
  <div aria-hidden="true" style="display: none"></div>
</template>

<script>
import { notificationManager } from '@/utils/notificationManager';

function readStoredRoleName() {
  return String(localStorage.getItem('role') || '').trim().toLowerCase();
}

function isAdminRoleName(roleName) {
  return roleName === 'admin';
}

function isSystemMigrationNotification(notification) {
  if (!notification || typeof notification !== 'object') return false;
  if (notification.kind === 'system-migration') return true;
  const mid = notification.data && notification.data.migrationId;
  if (!mid) return false;
  return (
    notification.type === 'warning' ||
    notification.type === 'loading' ||
    notification.type === 'success'
  );
}

export default {
  name: 'MigrationNotification',
  data() {
    return {
      pendingMigrations: [],
      isExecuting: false,
      notificationId: null,
      checkTimer: null,
      pollTimer: null,
      dismissedUntilReload: false,
    };
  },
  computed: {
    isAuthenticated() {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      return !!(token && userId);
    },
    /** Solo admin ve y ejecuta migraciones (alineado con backend). */
    isMigrationAdmin() {
      return isAdminRoleName(readStoredRoleName());
    },
  },
  mounted() {
    this.$root.$on('gotrading-user-role-updated', this.onUserRoleUpdated);
    this.scheduleMigrationCheck(400);
    this.pollTimer = setInterval(() => {
      this.scheduleMigrationCheck(0);
    }, 60000);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  },
  beforeDestroy() {
    this.$root.$off('gotrading-user-role-updated', this.onUserRoleUpdated);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.notificationId) {
      notificationManager.removeNotification(this.notificationId);
    }
  },
  watch: {
    isAuthenticated(newVal) {
      if (newVal) {
        this.scheduleMigrationCheck(800);
      } else {
        this.clearMigrationNotificationState();
      }
    },
    isMigrationAdmin(newVal) {
      if (newVal && this.isAuthenticated) {
        this.scheduleMigrationCheck(400);
      } else {
        this.clearMigrationNotificationState();
      }
    },
  },
  methods: {
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.scheduleMigrationCheck(200);
      }
    },
    onUserRoleUpdated() {
      if (this.isAuthenticated && this.isMigrationAdmin) {
        this.scheduleMigrationCheck(400);
      } else {
        this.clearMigrationNotificationState();
      }
    },
    scheduleMigrationCheck(delayMs = 400) {
      if (this.checkTimer) {
        clearTimeout(this.checkTimer);
      }
      this.checkTimer = setTimeout(() => {
        this.checkTimer = null;
        this.checkPendingMigrations();
      }, delayMs);
    },
    clearMigrationNotificationState() {
      notificationManager.removeNotificationsWhere(isSystemMigrationNotification);
      this.notificationId = null;
      this.pendingMigrations = [];
    },
    waitForNotificationManager(maxAttempts = 20, intervalMs = 150) {
      return new Promise((resolve) => {
        let attempts = 0;
        const tick = () => {
          if (notificationManager.getInstance && notificationManager.getInstance()) {
            resolve(true);
            return;
          }
          attempts += 1;
          if (attempts >= maxAttempts) {
            resolve(false);
            return;
          }
          setTimeout(tick, intervalMs);
        };
        tick();
      });
    },
    async checkPendingMigrations() {
      try {
        if (!this.isAuthenticated) {
          this.clearMigrationNotificationState();
          return;
        }
        if (!this.isMigrationAdmin) {
          this.clearMigrationNotificationState();
          return;
        }
        // Do not tear down the in-progress loading UI while executeMigrations runs.
        if (this.isExecuting) {
          return;
        }
        if (this.dismissedUntilReload) {
          return;
        }

        const { wencesApi } = await import('@/api/wencesApi');
        const response = await wencesApi.get('/api/migrations/pending', {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
          params: { _ts: Date.now() },
        });

        if (response.status === 401 || (response.data && !response.data.success && response.data.error === 'Authentication required')) {
          this.clearMigrationNotificationState();
          return;
        }
        if (response.status === 403) {
          this.clearMigrationNotificationState();
          return;
        }

        if (response.data && response.data.success) {
          const newPending = response.data.pending || [];

          if (newPending.length === 0) {
            // Always clear orphans (e.g. after reload lost notificationId, or migration finished elsewhere).
            this.clearMigrationNotificationState();
            this.dismissedUntilReload = false;
            return;
          }

          this.pendingMigrations = newPending;
          if (!this.notificationId) {
            await this.showMigrationNotification();
          }
        }
      } catch (error) {
        if (error?.response?.status === 403) {
          this.clearMigrationNotificationState();
          return;
        }
        console.error('Error checking pending migrations:', error);
      }
    },

    /** Escapa el texto que va dentro del html del popup. */
    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    /**
     * Todas las migraciones pendientes, una debajo de otra y en el orden en que se van
     * a ejecutar. Antes el popup mostraba solo la descripción de la primera, así que al
     * apretar «Actualizar» se corrían varias sin haberlas visto.
     */
    buildPendingMigrationsHtml() {
      const list = this.pendingMigrations || [];
      const esc = this.escapeHtml;
      const items = list
        .map((migration, index) => {
          const name = esc(migration.name || migration.migrationId);
          const version = migration.version ? esc(migration.version) : '';
          const id = esc(migration.migrationId);
          const description = migration.description ? esc(migration.description) : '';
          const scope = Array.isArray(migration.onlyDatabases) && migration.onlyDatabases.length
            ? esc(migration.onlyDatabases.join(', '))
            : '';
          return `
            <li class="migration-pending__item">
              <div class="migration-pending__head">
                <span class="migration-pending__order">${index + 1}</span>
                <span class="migration-pending__name">${name}</span>
                ${version ? `<span class="migration-pending__version">v${version}</span>` : ''}
              </div>
              <div class="migration-pending__id">${id}${
                scope ? ` &middot; ${scope}` : ''
              }</div>
              ${
                description
                  ? `<details class="migration-pending__details"><summary>${esc(
                      this.$t('Details') || 'Ver detalle'
                    )}</summary><div class="migration-pending__description">${description}</div></details>`
                  : ''
              }
            </li>`;
        })
        .join('');

      const intro =
        list.length === 1
          ? this.$t('One pending update; it runs when you click Update Now.') ||
            'Hay 1 actualización pendiente; se ejecuta al apretar Actualizar.'
          : (
              this.$t('migrationsPendingIntro') ||
              'Hay {count} actualizaciones pendientes. Se ejecutan todas, en este orden.'
            ).replace('{count}', String(list.length));

      return `
        <div class="migration-pending">
          <p class="migration-pending__intro">${esc(intro)}</p>
          <ol class="migration-pending__list">${items}</ol>
        </div>
        <style>
          .migration-pending__intro { margin: 0 0 .5rem; font-weight: 600; }
          .migration-pending__list { list-style: none; margin: 0; padding: 0; max-height: 19rem; overflow-y: auto; }
          .migration-pending__item { padding: .45rem 0; border-top: 1px solid rgba(0,0,0,.08); }
          .migration-pending__item:first-child { border-top: 0; }
          .migration-pending__head { display: flex; align-items: baseline; gap: .4rem; flex-wrap: wrap; }
          .migration-pending__order { flex: none; min-width: 1.25rem; height: 1.25rem; border-radius: 50%; background: rgba(0,0,0,.08); font-size: .7rem; display: inline-flex; align-items: center; justify-content: center; }
          .migration-pending__name { font-weight: 600; }
          .migration-pending__version { font-size: .72rem; padding: 0 .35rem; border-radius: .2rem; background: rgba(0,0,0,.08); }
          .migration-pending__id { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .68rem; opacity: .7; word-break: break-all; margin-left: 1.65rem; }
          .migration-pending__details { margin: .2rem 0 0 1.65rem; font-size: .78rem; }
          .migration-pending__details summary { cursor: pointer; opacity: .8; }
          .migration-pending__description { margin-top: .25rem; opacity: .9; }
        </style>`;
    },

    /** Texto plano equivalente, para donde no se pinte el html. */
    buildPendingMigrationsText() {
      const list = this.pendingMigrations || [];
      return list
        .map((m, i) => `${i + 1}. ${m.name || m.migrationId}${m.version ? ` (v${m.version})` : ''}`)
        .join('\n');
    },

    async showMigrationNotification() {
      if (this.pendingMigrations.length === 0) return;

      const ready = await this.waitForNotificationManager();
      if (!ready) {
        console.warn('[MigrationNotification] FloatingNotification no disponible; reintentando…');
        this.scheduleMigrationCheck(1000);
        return;
      }

      // Drop any stale migration toast before creating a fresh one.
      notificationManager.removeNotificationsWhere(isSystemMigrationNotification);
      this.notificationId = null;

      const migration = this.pendingMigrations[0];
      const count = this.pendingMigrations.length;
      const baseTitle =
        this.$t('System Update Available') || 'Actualización del Sistema Disponible';

      const notification = {
        kind: 'system-migration',
        // Con varias pendientes el título dice cuántas: apretar «Actualizar» corre todas.
        title: count > 1 ? `${baseTitle} (${count})` : baseTitle,
        type: 'warning',
        // Todas las pendientes, una debajo de otra; `message` queda como respaldo en texto.
        html: this.buildPendingMigrationsHtml(),
        message: this.buildPendingMigrationsText(),
        persistent: true,
        actions: [
          {
            text: this.$t('Update Now') || 'Actualizar Ahora',
            icon: 'fas fa-sync-alt',
            class: 'btn-primary',
            callback: () => {
              this.executeMigrations();
            },
          },
          {
            text: this.$t('Later') || 'Más Tarde',
            icon: 'fas fa-times',
            class: 'btn-outline-secondary',
            callback: () => {
              this.clearMigrationNotificationState();
              this.dismissedUntilReload = true;
            },
          },
        ],
        // El bloque `data` repetía el id y la versión de la primera migración debajo del
        // listado; ahora eso está en cada línea. Se conserva solo cuando hay una sola
        // pendiente, que es donde no molesta, y `kind` es la marca que usa el resto.
        ...(count === 1
          ? {
              data: {
                migrationId: migration.migrationId,
                version: migration.version,
              },
            }
          : {}),
      };

      const id = notificationManager.addNotification(notification);
      if (!id) {
        console.warn('[MigrationNotification] No se pudo mostrar la notificación; reintentando…');
        this.scheduleMigrationCheck(1000);
        return;
      }
      this.notificationId = id;
      this.dismissedUntilReload = false;
    },

    async pollMigrationUntilDone(migrationId, maxWaitMs = 30 * 60 * 1000) {
      const start = Date.now();
      const intervalMs = 2000;

      while (Date.now() - start < maxWaitMs) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));

        try {
          const { wencesApi } = await import('@/api/wencesApi');
          const response = await wencesApi.get(`/api/migrations/status/${migrationId}`, {
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
            params: { _ts: Date.now() },
          });
          const migration = response.data?.migration;
          const status = migration?.status;

          if (status === 'completed') {
            return migration;
          }
          if (status === 'failed') {
            throw new Error(migration?.error || 'Migration failed');
          }
        } catch (pollError) {
          if (pollError?.response?.status === 404) {
            continue;
          }
          throw pollError;
        }
      }

      throw new Error(
        this.$t('Migration is still running. Check again in a few minutes.') ||
          'La migración sigue en ejecución. Comprueba de nuevo en unos minutos.'
      );
    },

    async executeMigrations() {
      if (!this.isMigrationAdmin) {
        this.clearMigrationNotificationState();
        return;
      }
      if (!this.isAuthenticated) {
        if (this.notificationId) {
          notificationManager.convertNotification(
            this.notificationId,
            'error',
            this.$t('Authentication Required') || 'Autenticación Requerida',
            this.$t('You must be logged in to perform system updates.') ||
            'Debes estar conectado para realizar actualizaciones del sistema.'
          );
        }
        return;
      }

      if (this.pendingMigrations.length === 0) return;

      if (this.notificationId) {
        notificationManager.convertNotification(
          this.notificationId,
          'loading',
          this.$t('Updating System...') || 'Actualizando Sistema...',
          this.$t('Please wait while the system is being updated.') ||
          'Por favor espere mientras se actualiza el sistema.',
          {
            progress: 0,
            persistent: true,
            kind: 'system-migration',
            // Se limpia el listado: durante la ejecución manda el mensaje de progreso.
            html: '',
          }
        );
      }

      this.isExecuting = true;

      try {
        let completed = 0;
        const total = this.pendingMigrations.length;

        for (const migration of this.pendingMigrations) {
          try {
            if (this.notificationId) {
              const progress = Math.round((completed / total) * 100);
              notificationManager.updateProgress(this.notificationId, progress);
              notificationManager.updateMessage(
                this.notificationId,
                `${this.$t('Executing') || 'Ejecutando'}: ${migration.name}...`
              );
            }

            const { wencesApi } = await import('@/api/wencesApi');
            let migrationDone = false;

            try {
              const response = await wencesApi.post(
                `/api/migrations/execute/${migration.migrationId}`
              );

              if (response.data.completed) {
                migrationDone = true;
              } else if (response.data.processing || response.status === 202) {
                await this.pollMigrationUntilDone(migration.migrationId);
                migrationDone = true;
              } else if (response.data.success) {
                migrationDone = true;
              }
            } catch (startError) {
              const noResponse = !startError.response;
              const isTimeout =
                startError.code === 'ECONNABORTED' ||
                String(startError.message || '').toLowerCase().includes('timeout');
              if (noResponse || isTimeout) {
                await this.pollMigrationUntilDone(migration.migrationId);
                migrationDone = true;
              } else {
                throw startError;
              }
            }

            if (migrationDone) {
              completed++;
              // Una migración puede cambiar ModelDefinition (campos, tipos, isArray) sin que
              // haya deploy nuevo, así que la versión que invalida el caché no cambia y el
              // listado seguiría armando columnas con el esquema viejo hasta 12 h después.
              // El write-through de wencesApi solo cubre la edición manual de definiciones.
              try {
                const { clearModelDefinitionCache } = await import(
                  '@/utils/modelDefinitionCache'
                );
                clearModelDefinitionCache();
              } catch (cacheError) {
                console.error(
                  'No se pudo limpiar el caché de definiciones tras la migración:',
                  cacheError
                );
              }
            }
          } catch (error) {
            console.error(`Error executing migration ${migration.migrationId}:`, error);

            if (error.response?.status === 401 ||
                (error.response?.data?.error && error.response.data.error.includes('Authentication'))) {
              if (this.notificationId) {
                notificationManager.convertNotification(
                  this.notificationId,
                  'error',
                  this.$t('Authentication Required') || 'Autenticación Requerida',
                  this.$t('Your session has expired. Please log in again.') ||
                  'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
                );
              }
              this.isExecuting = false;
              return;
            }

            if (error.response?.status === 403) {
              this.clearMigrationNotificationState();
              this.isExecuting = false;
              return;
            }

            if (this.notificationId) {
              notificationManager.convertNotification(
                this.notificationId,
                'error',
                this.$t('Update Failed') || 'Error en la Actualización',
                error.response?.data?.error || error.message ||
                (this.$t('Error updating system') || 'Error al actualizar el sistema')
              );
            }

            this.isExecuting = false;
            return;
          }
        }

        if (this.notificationId) {
          notificationManager.convertNotification(
            this.notificationId,
            'success',
            this.$t('System Updated') || 'Sistema Actualizado',
            this.$t('System updated successfully') || 'Sistema actualizado exitosamente',
            {
              actions: [
                {
                  text: this.$t('Close') || 'Cerrar',
                  icon: 'fas fa-check',
                  class: 'btn-outline-success',
                  callback: () => {
                    this.clearMigrationNotificationState();
                  },
                },
              ],
              persistent: false,
              kind: 'system-migration',
              html: '',
            }
          );

          setTimeout(() => {
            this.clearMigrationNotificationState();
          }, 5000);
        } else {
          this.clearMigrationNotificationState();
        }

        this.pendingMigrations = [];
        this.dismissedUntilReload = false;
        this.scheduleMigrationCheck(2000);
      } catch (error) {
        console.error('Error executing migrations:', error);
        if (this.notificationId) {
          notificationManager.convertNotification(
            this.notificationId,
            'error',
            this.$t('Update Failed') || 'Error en la Actualización',
            error.response?.data?.error || error.message ||
            (this.$t('Error updating system') || 'Error al actualizar el sistema')
          );
        }
      } finally {
        this.isExecuting = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
